// Испытания мембран (десктоп) — таблица + графики накопленных стендовых
// замеров.  Данные вносятся с мобилы (operator app → «Испытание мембран»);
// здесь они только читаются, фильтруются по модели и визуализируются.
//
// Sidecar-модуль: endpoint /membrane-tests, requireAuth.  График — лёгкий
// inline-SVG (без сторонних библиотек), темизирован токенами MES.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/lib/AuthContext";
import { membraneTestsApi } from "@/lib/api";
import { hasPermission } from "@/lib/auth";
import type { MembraneTestRead, MembraneTestUpdate } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

// Метрики, доступные для построения графика.
// recovery (выход) и rejection (селективность) — вычисляемые.
const METRICS = [
  { key: "temperature", unit: "°C" },
  { key: "feed_tds", unit: "ppm" },
  { key: "feed_conductivity", unit: "мкСм/см" },
  { key: "pressure_in", unit: "бар" },
  { key: "pressure_out", unit: "бар" },
  { key: "permeate_pressure", unit: "бар" },
  { key: "concentrate_flow", unit: "м³/ч" },
  { key: "permeate_flow", unit: "л/мин" },
  { key: "tds", unit: "ppm" },
  { key: "conductivity", unit: "мкСм/см" },
  { key: "recovery", unit: "%" },
  { key: "rejection", unit: "%" },
] as const;

type MetricKey = (typeof METRICS)[number]["key"];

// Выход: пермиат / (пермиат + концентрат).
// Выход (recovery) = Q_пермиата / (Q_пермиата + Q_концентрата).  Расходы
// вводятся в РАЗНЫХ единицах с приборов (пермиат — л/мин, концентрат — м³/ч),
// поэтому перед делением приводим оба к л/ч: пермиат ×60, концентрат ×1000.
function recoveryPct(r: MembraneTestRead): number {
  const permLph = r.permeate_flow * 60; // л/мин → л/ч
  const concLph = r.concentrate_flow * 1000; // м³/ч → л/ч
  const denom = permLph + concLph;
  return denom > 0 ? (permLph / denom) * 100 : 0;
}

// Селективность: (1 − солёность_пермиата / солёность_входа).  Солёность можно
// задать как ТДС ИЛИ электропроводность — считаем по той паре, что заполнена
// (сначала ТДС, иначе электропроводность).  null — если пары нет.
function rejectionPct(r: MembraneTestRead): number | null {
  if (r.feed_tds != null && r.tds != null && r.feed_tds > 0) {
    return (1 - r.tds / r.feed_tds) * 100;
  }
  if (r.feed_conductivity != null && r.conductivity != null && r.feed_conductivity > 0) {
    return (1 - r.conductivity / r.feed_conductivity) * 100;
  }
  return null;
}

// Возвращает null для опциональных незаполненных метрик (напр. электропроводность
// внесена не в каждом замере) — такие точки на графике пропускаются.
function metricValue(r: MembraneTestRead, key: MetricKey): number | null {
  if (key === "recovery") return recoveryPct(r);
  if (key === "rejection") return rejectionPct(r);
  return r[key] ?? null;
}

export default function MembraneTests() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = hasPermission(user, "membrane_test.write");
  const canDelete = hasPermission(user, "membrane_test.delete");

  const [model, setModel] = useState<string>("");
  const [metric, setMetric] = useState<MetricKey>("permeate_flow");
  // Замер, открытый на редактирование (null — модалка закрыта).
  const [editing, setEditing] = useState<MembraneTestRead | null>(null);

  const models = useQuery({
    queryKey: ["membrane-tests", "models"],
    queryFn: () => membraneTestsApi.models(),
    staleTime: 60_000,
  });

  const tests = useQuery({
    queryKey: ["membrane-tests", "list", model],
    queryFn: () =>
      membraneTestsApi.list(model ? { membrane_model: model } : {}),
    refetchInterval: 15_000,
  });

  const remove = useMutation({
    mutationFn: (id: string) => membraneTestsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["membrane-tests"] }),
  });

  // API отдаёт newest-first; график строим в хронологическом порядке.
  const rows = tests.data?.items ?? [];
  const chrono = useMemo(() => [...rows].reverse(), [rows]);

  const exportCsv = () => {
    const header = [
      "created_at",
      "membrane_model",
      "serial",
      "area",
      "feed_tds",
      "feed_conductivity",
      "temperature",
      "pressure_in",
      "pressure_out",
      "permeate_pressure",
      "concentrate_flow",
      "permeate_flow",
      "tds",
      "conductivity",
      "pump_frequency",
      "recovery_pct",
      "rejection_pct",
      "note",
    ];
    const lines = chrono.map((r) =>
      [
        r.created_at,
        r.membrane_model,
        (r.serial ?? "").replace(/[\r\n;]+/g, " "),
        r.area,
        r.feed_tds ?? "",
        r.feed_conductivity ?? "",
        r.temperature,
        r.pressure_in,
        r.pressure_out,
        r.permeate_pressure,
        r.concentrate_flow,
        r.permeate_flow,
        r.tds ?? "",
        r.conductivity ?? "",
        r.pump_frequency ?? "",
        recoveryPct(r).toFixed(1),
        rejectionPct(r)?.toFixed(1) ?? "",
        (r.note ?? "").replace(/[\r\n;]+/g, " "),
      ].join(";"),
    );
    const blob = new Blob(["﻿" + [header.join(";"), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `membrane-tests-${model || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const metricUnit = METRICS.find((m) => m.key === metric)?.unit ?? "";

  return (
    <>
      <PageHeader
        eyebrow="membrane-tests"
        title={t("membraneTests.title")}
        description={t("membraneTests.description")}
        actions={
          <button
            className="btn"
            onClick={exportCsv}
            disabled={chrono.length === 0}
          >
            {t("membraneTests.exportCsv")}
          </button>
        }
      />

      {/* Фильтры. */}
      <section className="flex flex-wrap items-end gap-4 mt-6">
        <label className="flex flex-col gap-1">
          <span className="text-text-muted text-xs font-mono uppercase tracking-wider">
            {t("membraneTests.model")}
          </span>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="px-3 py-2 rounded-md bg-surface border border-border text-sm min-w-[200px]"
          >
            <option value="">{t("membraneTests.allModels")}</option>
            {(models.data?.models ?? []).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-text-muted text-xs font-mono uppercase tracking-wider">
            {t("membraneTests.chartMetric")}
          </span>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as MetricKey)}
            className="px-3 py-2 rounded-md bg-surface border border-border text-sm min-w-[200px]"
          >
            {METRICS.map((m) => (
              <option key={m.key} value={m.key}>
                {t(`membraneTests.metrics.${m.key}`)}
              </option>
            ))}
          </select>
        </label>

        <span className="text-text-muted text-sm ml-auto tabular-nums">
          {t("membraneTests.count", { count: rows.length })}
        </span>
      </section>

      {/* График. */}
      <section className="card p-5 mt-4">
        <h2 className="text-sm font-mono uppercase tracking-wider text-text-muted mb-3">
          {t(`membraneTests.metrics.${metric}`)} · {metricUnit}
        </h2>
        <LineChart
          points={chrono
            .map((r) => ({
              x: new Date(r.created_at).getTime(),
              y: metricValue(r, metric),
              label: `${formatDateTime(r.created_at)} · ${r.membrane_model}`,
            }))
            .filter(
              (p): p is { x: number; y: number; label: string } =>
                p.y !== null && Number.isFinite(p.y),
            )}
          unit={metricUnit}
        />
      </section>

      {/* Таблица. */}
      <section className="card overflow-x-auto mt-4">
        {tests.isLoading ? (
          <p className="p-6 text-text-muted text-sm">{t("common.loading")}</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-text-muted text-sm">{t("membraneTests.empty")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-3 py-3">{t("common.created")}</th>
                <th className="px-3 py-3">{t("membraneTests.model")}</th>
                <th className="px-3 py-3">{t("membraneTests.serial")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.colArea")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.feed_tds")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.feed_conductivity")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.temperature")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.pressure_in")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.pressure_out")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.permeate_pressure")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.concentrate_flow")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.permeate_flow")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.tds")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.conductivity")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.pumpFrequency")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.recovery")}</th>
                <th className="px-3 py-3 text-right">{t("membraneTests.metrics.rejection")}</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-surface-hover">
                  <td className="px-3 py-3 text-text-muted font-mono text-xs whitespace-nowrap">
                    {formatDateTime(r.created_at)}
                  </td>
                  <td className="px-3 py-3 font-medium">{r.membrane_model}</td>
                  <td className="px-3 py-3 font-mono text-xs text-text-muted">{r.serial ?? "—"}</td>
                  <td className="px-3 py-3 text-right tabular-nums font-mono text-text-muted">{r.area}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.feed_tds ?? "—"}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.feed_conductivity ?? "—"}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.temperature}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.pressure_in}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.pressure_out}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.permeate_pressure}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.concentrate_flow}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.permeate_flow}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.tds ?? "—"}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.conductivity ?? "—"}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.pump_frequency ?? "—"}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-accent-strong">
                    {recoveryPct(r).toFixed(1)}%
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-accent-strong">
                    {rejectionPct(r) == null ? "—" : `${rejectionPct(r)!.toFixed(1)}%`}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    {canEdit && (
                      <button
                        className="text-text-muted hover:text-accent text-xs mr-3"
                        title={t("membraneTests.edit")}
                        onClick={() => setEditing(r)}
                      >
                        ✎
                      </button>
                    )}
                    {canDelete && (
                      <button
                        className="text-text-muted hover:text-danger text-xs"
                        title={t("common.delete")}
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(t("membraneTests.confirmDelete"))) {
                            remove.mutate(r.id);
                          }
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {editing && (
        <EditModal row={editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

// ───────── Модалка редактирования замера ─────────
//
// Позволяет довнести пропущенное поле (напр. электропроводность) или
// исправить ошибку ввода в ранее сохранённом замере (PATCH /membrane-tests/:id).

// Обязательные числовые поля замера.  Солёность (ТДС/электропроводность входа и
// пермиата) сюда НЕ входит — она опциональна по паре (хотя бы одно из двух).
const EDIT_NUM_FIELDS = [
  { key: "temperature", unit: "°C" },
  { key: "pressure_in", unit: "бар" },
  { key: "pressure_out", unit: "бар" },
  { key: "concentrate_flow", unit: "м³/ч" },
  { key: "permeate_flow", unit: "л/мин" },
  { key: "permeate_pressure", unit: "бар" },
] as const;

type EditNumKey = (typeof EDIT_NUM_FIELDS)[number]["key"];

// "12,3" → 12.3; "" → null.
function parseNum(raw: string): number | null {
  const s = raw.replace(",", ".").trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
}

function EditModal({
  row,
  onClose,
}: {
  row: MembraneTestRead;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [model, setModel] = useState(row.membrane_model);
  const [area, setArea] = useState<400 | 440>(row.area === 440 ? 440 : 400);
  const [nums, setNums] = useState<Record<EditNumKey, string>>(() => {
    const out = {} as Record<EditNumKey, string>;
    for (const f of EDIT_NUM_FIELDS) out[f.key] = String(row[f.key]);
    return out;
  });
  const [feedTds, setFeedTds] = useState(
    row.feed_tds == null ? "" : String(row.feed_tds),
  );
  const [feedConductivity, setFeedConductivity] = useState(
    row.feed_conductivity == null ? "" : String(row.feed_conductivity),
  );
  const [tds, setTds] = useState(row.tds == null ? "" : String(row.tds));
  const [conductivity, setConductivity] = useState(
    row.conductivity == null ? "" : String(row.conductivity),
  );
  const [serial, setSerial] = useState(row.serial ?? "");
  const [pumpFrequency, setPumpFrequency] = useState(
    row.pump_frequency == null ? "" : String(row.pump_frequency),
  );
  const [note, setNote] = useState(row.note ?? "");
  const [error, setError] = useState<string | null>(null);

  // Esc закрывает модалку.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Модель непустая, обязательные числа парсятся, и в каждой паре солёности
  // (вход / пермиат) заполнено хотя бы одно из «ТДС / электропроводность».
  const feedReadingOk =
    parseNum(feedTds) !== null || parseNum(feedConductivity) !== null;
  const permeateReadingOk =
    parseNum(tds) !== null || parseNum(conductivity) !== null;
  const canSave =
    model.trim().length > 0 &&
    EDIT_NUM_FIELDS.every((f) => parseNum(nums[f.key]) !== null) &&
    feedReadingOk &&
    permeateReadingOk;

  const save = useMutation({
    mutationFn: () => {
      const patch: MembraneTestUpdate = {
        membrane_model: model.trim(),
        area,
        feed_tds: parseNum(feedTds), // null очищает поле
        feed_conductivity: parseNum(feedConductivity), // null очищает поле
        tds: parseNum(tds), // null очищает поле
        conductivity: parseNum(conductivity), // null очищает поле
        serial: serial.trim() || null,
        pump_frequency: parseNum(pumpFrequency),
        note: note.trim() || null,
      };
      for (const f of EDIT_NUM_FIELDS) patch[f.key] = parseNum(nums[f.key])!;
      return membraneTestsApi.update(row.id, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["membrane-tests"] });
      onClose();
    },
    onError: (e) =>
      setError(e instanceof Error ? e.message : t("membraneTests.editErr")),
  });

  const inputCls =
    "w-full mt-1 px-3 py-2 rounded-md bg-surface border border-border tabular-nums";
  const labelCls =
    "text-xs font-mono uppercase tracking-wider text-text-muted flex items-center justify-between gap-2";

  return (
    <div className="fixed inset-0 z-30 bg-page/70 backdrop-blur flex items-center justify-center p-6">
      <form
        className="card p-6 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-y-auto"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <header className="flex items-center justify-between">
          <div>
            <h2 className="text-lg">{t("membraneTests.editTitle")}</h2>
            <p className="text-text-muted text-xs font-mono mt-0.5">
              {formatDateTime(row.created_at)} · {row.membrane_model}
            </p>
          </div>
          <button type="button" className="btn text-xs" onClick={onClose}>
            ×
          </button>
        </header>

        {/* Модель + площадь. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className={labelCls}>{t("membraneTests.model")}</span>
            <input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>{t("membraneTests.colArea")}</span>
            <select
              value={area}
              onChange={(e) => setArea(Number(e.target.value) as 400 | 440)}
              className={inputCls}
            >
              <option value={400}>400 ft²</option>
              <option value={440}>440 ft²</option>
            </select>
          </label>
        </div>

        {/* Числовые показания. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {EDIT_NUM_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className={labelCls}>
                <span>{t(`membraneTests.metrics.${f.key}`)}</span>
                <span className="text-text-muted normal-case">{f.unit}</span>
              </span>
              <input
                type="number"
                inputMode="decimal"
                step="any"
                value={nums[f.key]}
                onChange={(e) =>
                  setNums((cur) => ({ ...cur, [f.key]: e.target.value }))
                }
                className={inputCls}
              />
            </label>
          ))}
          {/* Солёность входа: ТДС / электропроводность (хотя бы одно). */}
          <label className="block">
            <span className={labelCls}>
              <span>{t("membraneTests.metrics.feed_tds")}</span>
              <span className="text-text-muted normal-case">ppm</span>
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              value={feedTds}
              onChange={(e) => setFeedTds(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>
              <span>{t("membraneTests.metrics.feed_conductivity")}</span>
              <span className="text-text-muted normal-case">мкСм/см</span>
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              value={feedConductivity}
              onChange={(e) => setFeedConductivity(e.target.value)}
              className={inputCls}
            />
          </label>
          {/* Солёность пермиата: ТДС / электропроводность (хотя бы одно). */}
          <label className="block">
            <span className={labelCls}>
              <span>{t("membraneTests.metrics.tds")}</span>
              <span className="text-text-muted normal-case">ppm</span>
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              value={tds}
              onChange={(e) => setTds(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>
              <span>{t("membraneTests.metrics.conductivity")}</span>
              <span className="text-text-muted normal-case">мкСм/см</span>
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              value={conductivity}
              onChange={(e) => setConductivity(e.target.value)}
              className={inputCls}
            />
          </label>
          {/* Частота насоса (опц.). */}
          <label className="block">
            <span className={labelCls}>
              <span>{t("membraneTests.pumpFrequency")}</span>
              <span className="text-text-muted normal-case">Гц</span>
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              value={pumpFrequency}
              onChange={(e) => setPumpFrequency(e.target.value)}
              className={inputCls}
            />
          </label>
        </div>

        <p className="text-text-muted text-xs">{t("membraneTests.readingPairHint")}</p>

        {/* Серийник + заметка. */}
        <label className="block">
          <span className={labelCls}>{t("membraneTests.serial")}</span>
          <input
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            className={`${inputCls} font-mono`}
          />
        </label>
        <label className="block">
          <span className={labelCls}>{t("membraneTests.note")}</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className={inputCls}
          />
        </label>

        {error && (
          <p className="text-danger text-sm">{error}</p>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={save.isPending}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canSave || save.isPending}
          >
            {save.isPending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </form>
    </div>
  );
}

// ───────── Лёгкий inline-SVG график (без зависимостей) ─────────

function LineChart({
  points,
  unit,
}: {
  points: Array<{ x: number; y: number; label: string }>;
  unit: string;
}) {
  const { t } = useTranslation();
  const W = 900;
  const H = 280;
  const pad = { top: 16, right: 16, bottom: 28, left: 48 };

  if (points.length === 0) {
    return (
      <p className="text-text-muted text-sm py-10 text-center">
        {t("membraneTests.empty")}
      </p>
    );
  }

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  let minY = Math.min(...ys);
  let maxY = Math.max(...ys);
  // Небольшой запас сверху/снизу + защита от плоской линии.
  if (minY === maxY) {
    minY -= 1;
    maxY += 1;
  } else {
    const m = (maxY - minY) * 0.08;
    minY -= m;
    maxY += m;
  }

  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;

  const sx = (x: number) =>
    pad.left + (maxX === minX ? innerW / 2 : ((x - minX) / (maxX - minX)) * innerW);
  const sy = (y: number) =>
    pad.top + innerH - ((y - minY) / (maxY - minY)) * innerH;

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`)
    .join(" ");

  // 4 горизонтальные сетки + подписи по Y.
  const ticks = Array.from({ length: 5 }, (_, i) => minY + ((maxY - minY) * i) / 4);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto"
      style={{ maxHeight: 320 }}
      role="img"
    >
      {ticks.map((tv, i) => {
        const y = sy(tv);
        return (
          <g key={i}>
            <line
              x1={pad.left}
              x2={W - pad.right}
              y1={y}
              y2={y}
              stroke="var(--color-border)"
              strokeWidth={1}
            />
            <text
              x={pad.left - 8}
              y={y + 3}
              textAnchor="end"
              fontSize={10}
              fontFamily="var(--font-mono)"
              fill="var(--color-text-muted)"
            >
              {tv.toFixed(maxY - minY < 5 ? 1 : 0)}
            </text>
          </g>
        );
      })}

      <path d={path} fill="none" stroke="var(--color-accent-strong)" strokeWidth={2} />

      {points.map((p, i) => (
        <circle
          key={i}
          cx={sx(p.x)}
          cy={sy(p.y)}
          r={3.5}
          fill="var(--color-accent-strong)"
        >
          <title>
            {p.label}: {p.y.toFixed(2)} {unit}
          </title>
        </circle>
      ))}
    </svg>
  );
}
